/**
 * The one dialog in the app.
 *
 * ## Why this exists rather than `window.confirm`
 *
 * Three screens asked a destructive question through `window.confirm` — delete every
 * source and its catalog, clear the whole history, revoke a device that is in another
 * room. It worked, and it was still the wrong control:
 *
 * - It is unstyleable. The most consequential moment in the app was the one place that
 *   looked nothing like the app.
 * - Mobile browsers title it with the origin — "filmodasi.poyland.com diyor ki" — which
 *   reads like a website nagging rather than a household setting being changed.
 * - It blocks the main thread. The realtime socket's heartbeat, the player and every
 *   pending render stop until somebody taps, and on a TV nobody may tap for an hour.
 * - Packaged webviews may suppress it outright, in which case the promise resolves to
 *   `false` and the action silently does nothing — a destructive action that quietly
 *   refuses is worse than one that quietly proceeds, because nobody investigates.
 *
 * So: one primitive, spent by the PIN prompt and the three confirmations alike.
 *
 * ## What it does that a styled `<div>` does not
 *
 * Focus moves in on open and returns to whatever opened it on close — otherwise
 * dismissing a dialog leaves focus on `<body>`, and the next Tab starts from the top of
 * the page. Tab is trapped, Escape dismisses, and the backdrop is a click target only for
 * dismissible dialogs; a question with consequences should not be answerable by a stray
 * tap outside it.
 *
 * On a narrow viewport it becomes a bottom sheet, because a centred box on a phone puts
 * the buttons in the middle of the screen where no thumb is.
 *
 * ## It does reach the television, and that is fine
 *
 * The profile picker is one component for all three layouts, so the PIN dialog renders on
 * the TV too. The two focus systems do not collide, for reasons worth writing down rather
 * than rediscovering:
 *
 * - The trap only intercepts `Tab`, and a TV remote emits arrow keys and `XF86Back`
 *   (10009), never `Tab`. On the panel this handler is inert.
 * - `AutoFocusables` finds a dialog by `closest('[role="dialog"]')`, and that attribute is
 *   on the box rather than the backdrop, so the focusables inside are still recognised as
 *   living in a dialog.
 * - Both systems want the same element focused — the field, then the first control — so
 *   the one that runs second changes nothing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  title,
  children,
  onDismiss,
  labelledBy,
}: {
  title: string;
  children: ReactNode;
  /** Omitted for a dialog that must be answered rather than closed. */
  onDismiss?: () => void;
  /** Set when the box renders its own heading and this title is only the accessible name. */
  labelledBy?: string;
}) {
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const element = box.current;

    // Whatever the dialog says first, unless it offers a field — a text box that is
    // already focused saves a tap on a phone and a whole navigation on a remote.
    const first = element?.querySelector<HTMLElement>('input, textarea') ??
      element?.querySelector<HTMLElement>(FOCUSABLE) ?? element;
    first?.focus();

    return () => {
      /*
       * Restore focus — unless something else legitimately took it.
       *
       * The obvious guard, "only if focus is still inside the dialog", is exactly wrong,
       * and it silently did nothing for the first version of this component. React runs a
       * passive effect's cleanup *after* the node has been detached, and a focused element
       * that leaves the document hands focus back to `<body>`. So by the time this runs,
       * focus is never inside the dialog and the restore was always skipped: dismissing a
       * confirmation dropped the keyboard back to the top of the page.
       *
       * `body` is therefore the signal to restore, not the signal to leave alone. Anything
       * else holding focus is a deliberate move — an action that opened another screen —
       * and is left where it is. The opener is skipped if it has itself been removed,
       * which is the ordinary case for "revoke this device" answered yes.
       */
      const active = document.activeElement;
      const abandoned = !active || active === document.body || element?.contains(active);
      if (abandoned && opener && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && onDismiss) {
      e.stopPropagation();
      onDismiss();
      return;
    }
    if (e.key !== 'Tab') return;

    const element = box.current;
    if (!element) return;
    const items = [...element.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;

    // Wrap by hand. Without this, Tab walks out of the dialog and into the page behind it,
    // which is still rendered and still clickable-looking under a 65% black backdrop.
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="modal"
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        // The backdrop only, never a drag that began inside the box and ended outside it.
        if (onDismiss && e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        ref={box}
        className="modal__box"
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
      >
        {children}
      </div>
    </div>
  );
}

/** The buttons row. Its own component so every dialog puts them in the same order. */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="modal__actions">{children}</div>;
}

export interface ConfirmRequest {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Colours the confirming button as destructive. Cancel is first in the DOM either way,
   *  so it is what focus lands on and what Enter answers. */
  danger?: boolean;
}

interface PendingConfirm extends ConfirmRequest {
  settle: (answer: boolean) => void;
}

/**
 * `window.confirm`, kept as a one-line `await` at the call site.
 *
 *     const { confirm, dialog } = useConfirm();
 *     if (!(await confirm({ title: '…', danger: true }))) return;
 *     …
 *     return (<>…{dialog}</>);
 *
 * The shape is deliberate: replacing the old calls meant changing one line and adding one
 * node, so the guard could not be dropped by accident during the migration.
 */
export function useConfirm() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const live = useRef(true);

  /*
   * Armed on mount, disarmed on unmount — and the arming half is not optional.
   *
   * Written as cleanup-only, this hook worked exactly once. React's StrictMode mounts,
   * unmounts and remounts every component in development, so the cleanup ran during that
   * rehearsal, `live` stayed `false` for the rest of the component's life, and every
   * `confirm()` resolved `false` before rendering anything. The symptom was a delete
   * button that did nothing at all, with no error anywhere — the exact failure this hook
   * was written to replace `window.confirm` for.
   *
   * It is not a StrictMode artefact either: any remount of a kept-alive component, which
   * is what a route change back to the same screen is, would do the same in production.
   */
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const confirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        /*
         * An unmount while the question is open resolves `false` rather than leaving the
         * awaiting call suspended forever. False is the safe answer for every caller —
         * all of them are destructive — and a promise that never settles would hold the
         * calling closure, its component and its state alive for the life of the tab.
         */
        if (!live.current) {
          resolve(false);
          return;
        }
        setPending({
          ...request,
          settle: (answer) => {
            setPending(null);
            resolve(answer);
          },
        });
      }),
    [],
  );

  const dialog = pending ? (
    <Dialog title={pending.title} onDismiss={() => pending.settle(false)}>
      <h2 className="modal__title">{pending.title}</h2>
      {pending.body && <p className="modal__body">{pending.body}</p>}
      <DialogActions>
        <button type="button" onClick={() => pending.settle(false)}>
          {pending.cancelLabel ?? 'Vazgeç'}
        </button>
        <button
          type="button"
          className={pending.danger ? 'danger' : 'primary'}
          onClick={() => pending.settle(true)}
        >
          {pending.confirmLabel ?? 'Tamam'}
        </button>
      </DialogActions>
    </Dialog>
  ) : null;

  return { confirm, dialog };
}
